import { useEffect, type ReactNode } from "react";

/**
 * Minimal modal with backdrop, click-outside-to-close, and ESC-to-close.
 * Keeps focus unmanaged — simpler than a full focus trap, acceptable for
 * the modals we render today (Privacy, Terms, Auth) which are content- or
 * form-driven flows rather than focus-driven flows.
 *
 * Body scroll is locked while the modal is open so readers don't
 * accidentally scroll the page behind the modal on mobile.
 *
 * `dismissable=false` locks the modal open — no X button, no ESC, no
 * backdrop click. Used by auth confirm step to avoid the user losing the
 * 6-digit-code context by accidentally dismissing mid-flow.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  dismissable = true,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  dismissable?: boolean;
}) {
  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (dismissable && e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);

    // Lock page scroll; restore whatever was there previously on unmount.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose, dismissable]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      // Full-screen fixed overlay. `onMouseDown` on the root + stopPropagation
      // on the panel is the idiomatic "click outside to close" pattern — the
      // mousedown target, not the click target, is what differentiates a
      // drag-to-select inside the panel from an intentional backdrop click.
      onMouseDown={dismissable ? onClose : undefined}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4 sm:p-8"
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl bg-paper dark:bg-ink border border-margin dark:border-paper/10 shadow-2xl overflow-hidden"
      >
        {/* Header — sticky so the close button stays reachable while the
            user scrolls through a long legal document. */}
        <header className="flex items-center justify-between gap-4 px-6 py-4 border-b border-margin dark:border-paper/10">
          <h2 className="font-display text-lg font-semibold text-ink dark:text-paper">
            {title}
          </h2>
          {dismissable && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="inline-flex items-center justify-center h-8 w-8 rounded-full text-ink/50 dark:text-paper/50 hover:bg-ink/5 dark:hover:bg-paper/5 hover:text-ink dark:hover:text-paper transition-colors"
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" className="h-4 w-4">
                <line x1="3" y1="3" x2="13" y2="13" />
                <line x1="13" y1="3" x2="3" y2="13" />
              </svg>
            </button>
          )}
        </header>

        {/* Scrollable body. The caller decides inner spacing / typography. */}
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </div>
  );
}
